import React, { useState, useEffect, useRef } from "react";
import FrontendLayout from "../../../../components/frontend/FrontendLayout";
import UserCommon from "../../../../components/frontend/UserCommon";
import UserTabs from "../../../../components/frontend/UserTabs";
import ProgressBar from "../../../../components/frontend/ProgressBar";
import NiceSelect from "../../../../components/frontend/NiceSelect";
import Uploader from "../../../../components/frontend/Uploader";
import CommonAlertModal from "../../../../components/CommonAlertModal";
import { ToastContainer, toast } from "react-toastify";
import { useUserProfile } from "../../../../hooks/useUserProfile";
import { useNavigate, useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

interface FormData {
  plotImage: File[];
  plotImagePreview: string[];
  plotVideo: File[];
  plotVideoPreview: string[];
  droneFootage: string[];
  droneFootagePreview: string[];
  droneFootageType: "image" | "video" | "";
  postTitle: string;
  postedDate: string;
  price: string;
  totalPriceSqft: string;
  totalareasquarefeet: string;
  plotType: string;
  zone: string;
  sellerType: string;
  transactionType: string;
  plotDimensions: {
    east: string;
    west: string;
    north: string;
    south: string;
  };
  possessionStatus: string;
  widthOfRoadFacing: string;
  overlooking: string;
  plotDescription: string;
  growthPotentials: string;
  anyConstructionDone: string;
  status: {
    legal: boolean;
    infra: boolean;
  };
  loanAvailable: string;
  landAvailability: string;
  classification: string | null;
  numberOfFloors: string;
  boundaryWall: string;
  facingDirection: string;
  cornerPlot: string;
  fencingType: string;
  utilitiesAvailability: string;
  topography: string;
  floodZoneRisk: string;
  reraNumber: string;
  authorityApproved: string;
  encumbranceCertificate: string;
  encumbranceCertificateFile: File | null;
  mutationDone: string;
  pattaAvailable: string;
  soilType: string;
  locality: string;
  address: string;
  nearbyLandmark: string;
  customLandmark: string;
  highlights: string;
  longitude: string;
  latitude: string;
  gatedSociety: string;
  siteVisitAvailable: string;
  siteVisitLink: string;
}

interface Errors {
  [key: string]: string;
}

interface ApiResponse {
  success: boolean;
  AllLandMaster: {
    classifications: Array<{
      cls_id: number;
      cls_name: string;
      cls_status: number;
    }>;
    plotTypes: Array<{ pt_id: number; pt_name: string; pt_status: number }>;
    zones: Array<{ z_id: number; z_name: string; "Z-status": number }>;
    fencing: Array<{ fnc_id: number; fnc_name: string; fnc_status: number }>;
    facing: Array<{ fc_id: number; fc_name: string; fc_status: number }>;
    topography: Array<{ tp_id: number; tp_name: string; tp_status: number }>;
  };
  landDetails?: any;
}

const EditLand: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    plotImage: [],
    plotImagePreview: [],
    plotVideo: [],
    plotVideoPreview: [],
    droneFootage: [],
    droneFootagePreview: [],
    droneFootageType: "",
    postTitle: "",
    postedDate: "",
    price: "",
    totalPriceSqft: "",
    totalareasquarefeet: "",
    plotType: "",
    zone: "",
    sellerType: "",
    transactionType: "",
    plotDimensions: {
      east: "",
      west: "",
      north: "",
      south: "",
    },
    possessionStatus: "",
    widthOfRoadFacing: "",
    overlooking: "",
    plotDescription: "",
    growthPotentials: "",
    anyConstructionDone: "",
    status: {
      legal: false,
      infra: false,
    },
    loanAvailable: "",
    landAvailability: "",
    classification: "",
    numberOfFloors: "",
    boundaryWall: "",
    facingDirection: "",
    cornerPlot: "",
    fencingType: "",
    utilitiesAvailability: "",
    topography: "",
    floodZoneRisk: "",
    reraNumber: "",
    authorityApproved: "",
    encumbranceCertificate: "",
    encumbranceCertificateFile: null,
    mutationDone: "",
    pattaAvailable: "",
    soilType: "",
    locality: "",
    address: "",
    nearbyLandmark: "",
    customLandmark: "",
    highlights: "",
    longitude: "",
    latitude: "",
    gatedSociety: "",
    siteVisitAvailable: "",
    siteVisitLink: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [alertModal, setAlertModal] = useState<{
    show: boolean;
    message: string;
  }>({
    show: false,
    message: "",
  });

  const [apiData, setApiData] = useState<ApiResponse["AllLandMaster"] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingVideos, setExistingVideos] = useState<string[]>([]);
  const [existingDroneFootage, setExistingDroneFootage] = useState<string>("");
  const [existingDroneFootageType, setExistingDroneFootageType] = useState<
    "image" | "video" | ""
  >("");

  const plotImageInputRef = useRef<HTMLInputElement>(null);
  const plotVideoInputRef = useRef<HTMLInputElement>(null);
  const droneFootageInputRef = useRef<HTMLInputElement>(null);
  const encumbranceCertificateInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("frontendUserToken");
  const userType = localStorage.getItem("frontendUserType");
  const profile = useUserProfile(token);

  useEffect(() => {
    if (userType !== "1" && userType !== "2") {
      navigate("/403");
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Fetch master data
        const masterResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/alllandmaster/list`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const masterData: ApiResponse = await masterResponse.json();

        if (!masterData.success) {
          throw new Error("Failed to load dropdown options");
        }
        setApiData(masterData.AllLandMaster);

        // Fetch land details
        const landResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/landadd/edit/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const landData = await landResponse.json();

        if (!landData.success) {
          throw new Error("Failed to load land details");
        }

        const landDetails = landData.landadd;

        // Set existing media
        if (landDetails.ln_image && Array.isArray(landDetails.ln_image)) {
          const imageUrls = (landDetails.ln_image as string[]).map(
            (filename) =>
              `${process.env.REACT_APP_API_BASE_URL}/uploads/lands/${filename}`
          );
          setExistingImages(imageUrls);
        }

        if (landDetails.ln_video) {
          setExistingVideos([landDetails.ln_video]);
        }
        if (landDetails.ln_drone_footage) {
          setExistingDroneFootage(landDetails.ln_drone_footage);
          setExistingDroneFootageType(
            landDetails.ln_drone_footage_type === "video" ? "video" : "image"
          );
        }

        // Set form data
        setFormData({
          plotImage: [],
          plotImagePreview: [],
          plotVideo: [],
          plotVideoPreview: [],
          droneFootage: [],
          droneFootagePreview: [],
          droneFootageType: "",
          postTitle: landDetails.ln_title || "",
          postedDate: landDetails.ln_post_date
            ? (() => {
                const date = new Date(landDetails.ln_post_date);
                const formattedDate = date.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "numeric",
                  year: "numeric",
                });
                // Convert DD/MM/YYYY to YYYY-MM-DD
                const [day, month, year] = formattedDate.split("/");
                return `${year}-${month.padStart(2, "0")}-${day.padStart(
                  2,
                  "0"
                )}`;
              })()
            : "",
          price: landDetails.ln_price || "",
          totalPriceSqft: landDetails.ln_total_area_sqft || "",
          totalareasquarefeet: landDetails.ln_total_sqft_area || "",
          plotType: landDetails.ln_plottype || "",
          zone: landDetails.ln_zone || "",
          sellerType: landDetails.ln_seller_type || "",
          transactionType: landDetails.ln_transaction_type || "",
          plotDimensions: {
            east: landDetails.ln_dimension_east || "",
            west: landDetails.ln_dimension_west || "",
            north: landDetails.ln_dimension_north || "",
            south: landDetails.ln_dimension_south || "",
          },
          possessionStatus: landDetails.ln_possesion_status || "",
          widthOfRoadFacing: landDetails.ln_width_roadfacing || "",
          overlooking: landDetails.ln_overlooking || "",
          plotDescription: landDetails.ln_desc || "",
          growthPotentials: landDetails.ln_growth || "",
          anyConstructionDone: landDetails.ln_anyconstruction || "",
          status: {
            legal: landDetails.ln_legal_status === "Legal",
            infra: landDetails.ln_infrastructural_status === "Infra",
          },
          loanAvailable: landDetails.ln_loan || "",
          landAvailability:
            landDetails.ln_land_avail === "Avail"
              ? "yes"
              : landDetails.ln_land_avail === "Sold"
              ? "soldout"
              : "",
          classification: landDetails.ln_classification || "",
          numberOfFloors: landDetails.ln_allowed_floors || "",
          boundaryWall: landDetails.ln_boundarywall || "",
          facingDirection: landDetails.ln_facing || "",
          cornerPlot: landDetails.ln_corner || "",
          fencingType: landDetails.ln_fencing_type || "",
          utilitiesAvailability:
            landDetails.ln_electric_supply || landDetails.ln_water_supply || "",
          topography: landDetails.ln_topography || "",
          floodZoneRisk: landDetails.ln_water_looging || "",
          reraNumber: landDetails.ln_rerano || "",
          authorityApproved: landDetails.ln_authority_approve || "",
          encumbranceCertificate: landDetails.ln_encumbrance_status || "",
          encumbranceCertificateFile: null,
          mutationDone: landDetails.ln_mutation || "",
          pattaAvailable: landDetails.ln_patta_avail || "",
          soilType: landDetails.ln_soiltype || "",
          locality: landDetails.ln_locality || "",
          address: landDetails.ln_address || "",
          nearbyLandmark: landDetails.ln_nearby_landmark || "",
          customLandmark: landDetails.ln_nearby_landmark || "",
          highlights: landDetails.ln_hilights || "",
          longitude: landDetails.ln_map_long || "",
          latitude: landDetails.ln_map_lat || "",
          gatedSociety: landDetails.ln_gated || "",
          siteVisitAvailable: landDetails.ln_sitevisit_avail || "",
          siteVisitLink: landDetails.ln_sitevisit_link || "",
        });
      } catch (error: any) {
        setApiError(error.message || "Failed to load data");
        toast.error(error.message || "Failed to load data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userType, navigate, token, id]);

  const validateStep = (step: number): boolean => {
    const newErrors: Errors = {};
    const numberRegex = /^\d+(\.\d+)?$/;
    const positiveNumberRegex = /^[1-9]\d*(\.\d+)?$/;

    if (step === 1) {
      // if (formData.plotImage.length === 0 && existingImages.length === 0)
      //   newErrors.plotImage = "At least one plot image is required";
      if (!formData.postTitle) newErrors.postTitle = "Post title is required";
      else if (formData.postTitle.length > 150)
        newErrors.postTitle = "Post title cannot exceed 150 characters";
      // if (!formData.postedDate)
      //   newErrors.postedDate = "Posted date is required";
      if (!formData.price) newErrors.price = "Price is required";
      else if (
        !numberRegex.test(formData.price) ||
        parseFloat(formData.price) < 0
      )
        newErrors.price = "Price must be a positive number";
      else if (formData.price.length > 100)
        newErrors.price = "Price cannot exceed 100 characters";
      if (!formData.totalPriceSqft)
        newErrors.totalPriceSqft = "Price per sq. ft. is required";
      else if (
        !numberRegex.test(formData.totalPriceSqft) ||
        parseFloat(formData.totalPriceSqft) < 0
      )
        newErrors.totalPriceSqft =
          "Price per sq. ft. must be a positive number";
      else if (formData.totalPriceSqft.length > 100)
        newErrors.totalPriceSqft =
          "Price per sq. ft. cannot exceed 100 characters";
      if (
        !newErrors.price && // only check if price is valid
        !newErrors.totalPriceSqft // only check if per sq ft is valid
      ) {
        const price = parseFloat(formData.price);
        const pricePerSqft = parseFloat(formData.totalPriceSqft);
        if (pricePerSqft >= price) {
          newErrors.totalPriceSqft =
            "Price per sq. ft. must be less than the total Price";
        }
      }
      if (!formData.totalareasquarefeet) {
        newErrors.totalareasquarefeet = "Total area sq. ft. is required";
      } else if (
        !numberRegex.test(formData.totalareasquarefeet) ||
        parseFloat(formData.totalareasquarefeet) < 0
      ) {
        newErrors.totalareasquarefeet =
          "Total area sq. ft. must be a positive number";
      } else if (formData.totalareasquarefeet.length > 100) {
        newErrors.totalareasquarefeet =
          "Total area sq. ft. cannot exceed 100 characters";
      }
    } else if (step === 2) {
      if (!formData.plotType) newErrors.plotType = "Plot type is required";
      if (!formData.zone) newErrors.zone = "Zone is required";
      if (!formData.sellerType)
        newErrors.sellerType = "Seller type is required";
      if (!formData.transactionType)
        newErrors.transactionType = "Transaction type is required";

      let plotDimensionErrors = [];

      // Check which are empty
      const emptyDimensions = [];
      if (!formData.plotDimensions.east) emptyDimensions.push("East");
      if (!formData.plotDimensions.west) emptyDimensions.push("West");
      if (!formData.plotDimensions.north) emptyDimensions.push("North");
      if (!formData.plotDimensions.south) emptyDimensions.push("South");

      if (emptyDimensions.length === 4) {
        // All empty — show general message
        newErrors.plotDimensions = "Plot dimensions are required.";
      } else {
        // Otherwise, check individually
        if (!formData.plotDimensions.east && emptyDimensions.length < 4) {
          plotDimensionErrors.push("East dimension is required");
        } else if (
          formData.plotDimensions.east &&
          (!positiveNumberRegex.test(formData.plotDimensions.east) ||
            parseFloat(formData.plotDimensions.east) < 1 ||
            parseFloat(formData.plotDimensions.east) > 5000)
        ) {
          plotDimensionErrors.push("East must be between 1 and 5000");
        }

        if (!formData.plotDimensions.west && emptyDimensions.length < 4) {
          plotDimensionErrors.push("West dimension is required");
        } else if (
          formData.plotDimensions.west &&
          (!positiveNumberRegex.test(formData.plotDimensions.west) ||
            parseFloat(formData.plotDimensions.west) < 1 ||
            parseFloat(formData.plotDimensions.west) > 5000)
        ) {
          plotDimensionErrors.push("West must be between 1 and 5000");
        }

        if (!formData.plotDimensions.north && emptyDimensions.length < 4) {
          plotDimensionErrors.push("North dimension is required");
        } else if (
          formData.plotDimensions.north &&
          (!positiveNumberRegex.test(formData.plotDimensions.north) ||
            parseFloat(formData.plotDimensions.north) < 1 ||
            parseFloat(formData.plotDimensions.north) > 5000)
        ) {
          plotDimensionErrors.push("North must be between 1 and 5000");
        }

        if (!formData.plotDimensions.south && emptyDimensions.length < 4) {
          plotDimensionErrors.push("South dimension is required");
        } else if (
          formData.plotDimensions.south &&
          (!positiveNumberRegex.test(formData.plotDimensions.south) ||
            parseFloat(formData.plotDimensions.south) < 1 ||
            parseFloat(formData.plotDimensions.south) > 5000)
        ) {
          plotDimensionErrors.push("South must be between 1 and 5000");
        }

        if (plotDimensionErrors.length > 0) {
          newErrors.plotDimensions = plotDimensionErrors.join(", ");
        }
      }

      if (formData.possessionStatus && formData.possessionStatus.length > 50) {
        newErrors.possessionStatus =
          "Possession status cannot exceed 50 characters";
      }
      if (!formData.widthOfRoadFacing)
        newErrors.widthOfRoadFacing = "Width of road facing is required";
      else if (
        !numberRegex.test(formData.widthOfRoadFacing) ||
        parseFloat(formData.widthOfRoadFacing) < 0
      )
        newErrors.widthOfRoadFacing =
          "Width of road facing must be a positive number";
      else if (formData.widthOfRoadFacing.length > 100)
        newErrors.widthOfRoadFacing =
          "Width of road facing cannot exceed 100 characters";
      if (formData.overlooking && formData.overlooking.length > 100) {
        newErrors.overlooking = "Overlooking cannot exceed 100 characters";
      }
      if (!formData.plotDescription)
        newErrors.plotDescription = "Plot description is required";
      if (
        formData.numberOfFloors &&
        (!numberRegex.test(formData.numberOfFloors) ||
          parseFloat(formData.numberOfFloors) < 0 ||
          formData.numberOfFloors.length > 50)
      )
        newErrors.numberOfFloors =
          "Number of floors must be a positive number and not exceed 50 characters";
      // if (!formData.boundaryWall)
      //   newErrors.boundaryWall = "Boundary wall status is required";
      if (!formData.facingDirection)
        newErrors.facingDirection = "Facing direction is required";
      if (!formData.cornerPlot)
        newErrors.cornerPlot = "Corner plot status is required";
      // if (!formData.fencingType)
      //   newErrors.fencingType = "Fencing type is required";
      if (!formData.utilitiesAvailability)
        newErrors.utilitiesAvailability = "Utilities availability is required";
      // if (!formData.topography) newErrors.topography = "Topography is required";
      // if (!formData.floodZoneRisk)
      //   newErrors.floodZoneRisk = "Flood zone risk is required";
      if (formData.reraNumber && formData.reraNumber.length > 100)
        newErrors.reraNumber = "RERA number cannot exceed 100 characters";
      if (!formData.authorityApproved)
        newErrors.authorityApproved = "Authority approval is required";
      if (!formData.encumbranceCertificate)
        newErrors.encumbranceCertificate =
          "Encumbrance certificate status is required";
      if (!formData.mutationDone)
        newErrors.mutationDone = "Mutation status is required";
      if (!formData.pattaAvailable)
        newErrors.pattaAvailable = "Patta availability is required";
      // if (!formData.soilType) newErrors.soilType = "Soil type is required";
      if (!formData.anyConstructionDone)
        newErrors.anyConstructionDone = "Construction status is required";
      if (!formData.status.legal && !formData.status.infra)
        newErrors.status = "At least one status must be selected";
      if (!formData.loanAvailable)
        newErrors.loanAvailable = "Loan availability is required";
      if (!formData.landAvailability)
        newErrors.landAvailability = "Land availability is required";
      if (!formData.classification)
        newErrors.classification = "Classification is required";
    } else if (step === 3) {
      if (!formData.locality) newErrors.locality = "Locality is required";
      else if (formData.locality.length > 100)
        newErrors.locality = "Locality cannot exceed 100 characters";

      if (!formData.address) newErrors.address = "Address is required";

      if (!formData.nearbyLandmark)
        newErrors.nearbyLandmark = "Nearby landmark is required";
      else if (formData.nearbyLandmark === "custom" && !formData.customLandmark)
        newErrors.nearbyLandmark = "Custom landmark is required";
      else if (
        formData.nearbyLandmark === "custom" &&
        formData.customLandmark.length > 100
      )
        newErrors.nearbyLandmark =
          "Custom landmark cannot exceed 100 characters";
    } else if (step === 4) {
      // helper for coordinate
      const cleanCoordinate = (value: string) => {
        if (!value) return "";
        return value
          .replace(/[°\s]/g, "") // remove degree symbol and spaces
          .replace(/[NnEe]/g, "") // if you assume N/E are positive
          .replace(/[SsWw]/g, "-"); // S/W → negative sign
      };

      const mapErrors = [];

      const longitudeRaw = formData.longitude;
      const latitudeRaw = formData.latitude;

      const longitudeClean = cleanCoordinate(longitudeRaw);
      const latitudeClean = cleanCoordinate(latitudeRaw);

      if (longitudeRaw) {
        if (
          !numberRegex.test(longitudeClean) ||
          parseFloat(longitudeClean) < -180 ||
          parseFloat(longitudeClean) > 180
        ) {
          mapErrors.push("Longitude must be between -180 and 180");
        }
      }

      if (latitudeRaw) {
        if (
          !numberRegex.test(latitudeClean) ||
          parseFloat(latitudeClean) < -90 ||
          parseFloat(latitudeClean) > 90
        ) {
          mapErrors.push("Latitude must be between -90 and 90");
        }
      }

      if (mapErrors.length > 0) {
        newErrors.map = mapErrors.join(", ");
      }

      if (!formData.gatedSociety)
        newErrors.gatedSociety = "Gated society status is required";

      if (formData.siteVisitAvailable === "yes") {
        if (formData.siteVisitLink) {
          const urlPattern =
            /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-./?%&=]*)?$/i;
          if (!urlPattern.test(formData.siteVisitLink)) {
            newErrors.siteVisitLink = "Site visit link must be a valid URL";
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    // Character limits
    if (name === "postTitle" && value.length > 150) return;
    if (name === "price" && value.length > 100) return;
    if (name === "totalPriceSqft" && value.length > 100) return;
    if (name === "totalareasquarefeet" && value.length > 100) return;
    if (name === "possessionStatus" && value.length > 50) return;
    if (name === "widthOfRoadFacing" && value.length > 100) return;
    if (name === "overlooking" && value.length > 100) return;
    if (name === "numberOfFloors" && value.length > 50) return;
    if (name === "reraNumber" && value.length > 100) return;
    if (name === "locality" && value.length > 100) return;
    if (name === "nearbyLandmark" && value.length > 100) return;

    setFormData((prev) => {
      let updated = { ...prev, [name]: value };

      // ✅ Auto-calculate price per sqft if price or total area changes
      if (
        (name === "price" || name === "totalareasquarefeet") &&
        updated.price &&
        updated.totalareasquarefeet &&
        !isNaN(Number(updated.price)) &&
        !isNaN(Number(updated.totalareasquarefeet))
      ) {
        const price = parseFloat(updated.price);
        const area = parseFloat(updated.totalareasquarefeet);
        if (area > 0) {
          updated.totalPriceSqft = (price / area).toFixed(2);
        } else {
          updated.totalPriceSqft = "";
        }
      }

      // For checkboxes
      if (type === "checkbox") {
        const target = e.target as HTMLInputElement;
        updated = {
          ...updated,
          status: {
            ...prev.status,
            [name]: target.checked,
          },
        };
      }

      // For nested plotDimensions
      if (name.includes("plotDimensions.")) {
        const dimension = name.split(".")[1];
        updated = {
          ...updated,
          plotDimensions: {
            ...prev.plotDimensions,
            [dimension]: value,
          },
        };
      }

      return updated;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (!files) return;

    const fileArray = Array.from(files);
    let updates: any = {};
    let isValid = true;

    if (name === "plotImage") {
      const existingFiles = formData.plotImage;
      const existingPreviews = formData.plotImagePreview;

      if (existingFiles.length + fileArray.length > 8) {
        setAlertModal({
          show: true,
          message: "You can upload a maximum of 8 images.",
        });
        isValid = false;
      } else {
        // 🔍 Separate checks:
        const nonImageFiles = fileArray.filter(
          (file) => !file.type.startsWith("image/")
        );
        const oversizeFiles = fileArray.filter(
          (file) =>
            file.type.startsWith("image/") && file.size > 5 * 1024 * 1024
        );

        if (nonImageFiles.length > 0) {
          setAlertModal({
            show: true,
            message: "Only image files are allowed. Some files were rejected.",
          });
          isValid = false;
        } else if (oversizeFiles.length > 0) {
          setAlertModal({
            show: true,
            message: "Each image must be under 5 MB. Some files were rejected.",
          });
          isValid = false;
        } else {
          const validFiles = fileArray.filter(
            (file) =>
              file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024
          );

          updates.plotImage = validFiles;
          updates.plotImagePreview = validFiles.map((file) =>
            URL.createObjectURL(file)
          );

          setExistingImages([]);
        }
      }

      if (!isValid && plotImageInputRef.current) {
        plotImageInputRef.current.value = "";
      }
    } else if (name === "plotVideo") {
      const existingFiles = formData.plotVideo;
      const existingPreviews = formData.plotVideoPreview;

      if (existingFiles.length + fileArray.length > 2) {
        setAlertModal({
          show: true,
          message: "You can upload a maximum of 2 videos.",
        });
        isValid = false;
      } else {
        // ✅ Separate checks:
        const invalidFiles = fileArray.filter(
          (file) =>
            !file.type.startsWith("video/") && !file.type.startsWith("image/")
        );
        const oversizeFiles = fileArray.filter(
          (file) =>
            (file.type.startsWith("video/") ||
              file.type.startsWith("image/")) &&
            file.size > 60 * 1024 * 1024
        );

        if (invalidFiles.length > 0) {
          setAlertModal({
            show: true,
            message:
              "Only video or image files are allowed. Some files were rejected.",
          });
          isValid = false;
        } else if (oversizeFiles.length > 0) {
          setAlertModal({
            show: true,
            message: "Each file must be under 60 MB. Some files were rejected.",
          });
          isValid = false;
        } else {
          const validFiles = fileArray.filter(
            (file) =>
              (file.type.startsWith("video/") ||
                file.type.startsWith("image/")) &&
              file.size <= 60 * 1024 * 1024
          );

          updates.plotVideo = [...existingFiles, ...validFiles];
          updates.plotVideoPreview = [
            ...existingPreviews,
            ...validFiles.map((file) => URL.createObjectURL(file)),
          ];
        }
      }

      if (!isValid && plotVideoInputRef.current) {
        plotVideoInputRef.current.value = "";
      }
    } else if (name === "droneFootage") {
      const file = fileArray[0];
      if (!file) return;

      if (!file.type.startsWith("video/")) {
        setAlertModal({
          show: true,
          message: "Only video file is allowed for drone footage.",
        });
        isValid = false;
      } else if (file.size > 10 * 1024 * 1024) {
        setAlertModal({
          show: true,
          message: "Drone footage must be under 10 MB.",
        });
        isValid = false;
      } else {
        const previewUrl = URL.createObjectURL(file);

        updates.droneFootage = [file];
        updates.droneFootagePreview = [previewUrl];
        updates.droneFootageType = "video";
      }

      if (!isValid && droneFootageInputRef?.current) {
        droneFootageInputRef.current.value = "";
      }
    } else if (name === "encumbranceCertificateFile") {
      const file = fileArray[0];
      if (!file) return;

      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (!validTypes.includes(file.type)) {
        setAlertModal({
          show: true,
          message:
            "Only PDF or image files (jpeg, jpg, png) are allowed for the Encumbrance Certificate.",
        });
        isValid = false;
      } else if (file.size > 5 * 1024 * 1024) {
        setAlertModal({
          show: true,
          message: "Encumbrance Certificate must be under 5 MB.",
        });
        isValid = false;
      } else {
        updates.encumbranceCertificateFile = file;
      }

      if (!isValid && encumbranceCertificateInputRef?.current) {
        encumbranceCertificateInputRef.current.value = "";
      }
    }

    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));

    // Clear file-related errors
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDeleteFile = (
    field: "plotImage" | "plotVideo",
    index: number
  ) => {
    setFormData((prev) => {
      const newFiles = [...prev[field]];
      const newPreviews =
        field === "plotImage"
          ? [...prev.plotImagePreview]
          : [...prev.plotVideoPreview];

      newFiles.splice(index, 1);
      newPreviews.splice(index, 1);

      if (newFiles.length === 0) {
        if (field === "plotImage" && plotImageInputRef.current) {
          plotImageInputRef.current.value = "";
        } else if (field === "plotVideo" && plotVideoInputRef.current) {
          plotVideoInputRef.current.value = "";
        }
      }

      return {
        ...prev,
        [field]: newFiles,
        [`${field}Preview`]: newPreviews,
      };
    });
  };

  const handleDeleteExistingImage = (index: number) => {
    setExistingImages((prev) => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleDeleteExistingVideo = (index: number) => {
    setExistingVideos((prev) => {
      const newVideos = [...prev];
      newVideos.splice(index, 1);
      return newVideos;
    });
  };

  const handleDeleteExistingDroneFootage = () => {
    setExistingDroneFootage("");
    setExistingDroneFootageType("");
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    } else {
      if (validateStep(currentStep)) {
        setCurrentStep(step);
      } else {
        toast.dismiss();
        toast.error(
          "Please complete all required fields in the current step before proceeding."
        );
      }
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep((prev) => prev + 1);
      }
    } else {
      toast.dismiss();
      toast.error(
        "Please complete all required fields in the current step before proceeding."
      );
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (validateStep(4)) {
      try {
        setIsLoading(true);

        const formDataToSend = new FormData();

        // Append the JSON fields
        formDataToSend.append("ln_title", formData.postTitle);
        formDataToSend.append("ln_post_date", formData.postedDate);
        formDataToSend.append("ln_price", formData.price);
        formDataToSend.append("ln_total_area_sqft", formData.totalPriceSqft);
        formDataToSend.append(
          "ln_total_sqft_area",
          formData.totalareasquarefeet
        );
        formDataToSend.append("ln_plottype", formData.plotType);
        formDataToSend.append(
          "ln_anyconstruction",
          formData.anyConstructionDone
        );
        formDataToSend.append(
          "ln_legal_status",
          formData.status.legal ? "Legal" : "Not Legal"
        );
        formDataToSend.append(
          "ln_infrastructural_status",
          formData.status.infra ? "Infra" : "No Infra"
        );
        formDataToSend.append("ln_boundarywall", formData.boundaryWall);
        formDataToSend.append("ln_overlooking", formData.overlooking);
        formDataToSend.append("ln_transaction_type", formData.transactionType);
        formDataToSend.append("ln_locality", formData.locality);
        formDataToSend.append("ln_address", formData.address);
        formDataToSend.append(
          "ln_width_roadfacing",
          formData.widthOfRoadFacing
        );
        formDataToSend.append("ln_desc", formData.plotDescription);
        formDataToSend.append("ln_hilights", formData.highlights);
        formDataToSend.append("ln_possesion_status", formData.possessionStatus);
        formDataToSend.append("ln_map_long", formData.longitude);
        formDataToSend.append("ln_map_lat", formData.latitude);
        formDataToSend.append("ln_seller_type", formData.sellerType);
        formDataToSend.append("ln_zone", formData.zone?.toString() || "");
        formDataToSend.append("ln_growth", formData.growthPotentials);
        formDataToSend.append("ln_gated", formData.gatedSociety);
        formDataToSend.append("ln_loan", formData.loanAvailable);
        formDataToSend.append(
          "ln_land_avail",
          formData.landAvailability === "yes" ? "Avail" : "Sold"
        );
        formDataToSend.append(
          "ln_classification",
          formData.classification?.toString() || ""
        );
        formDataToSend.append("ln_facing", formData.facingDirection);
        formDataToSend.append("ln_corner", formData.cornerPlot);
        formDataToSend.append("ln_fencing_type", formData.fencingType);
        formDataToSend.append(
          "ln_electric_supply",
          formData.utilitiesAvailability
        );
        formDataToSend.append(
          "ln_water_supply",
          formData.utilitiesAvailability
        );
        formDataToSend.append(
          "ln_nearby_landmark",
          formData.nearbyLandmark === "custom"
            ? formData.customLandmark
            : formData.nearbyLandmark
        );
        formDataToSend.append("ln_topography", formData.topography);
        formDataToSend.append("ln_water_looging", formData.floodZoneRisk);
        formDataToSend.append("ln_rerano", formData.reraNumber);
        formDataToSend.append(
          "ln_authority_approve",
          formData.authorityApproved
        );
        formDataToSend.append(
          "ln_encumbrance_status",
          formData.encumbranceCertificate
        );
        formDataToSend.append("ln_mutation", formData.mutationDone);
        formDataToSend.append("ln_patta_avail", formData.pattaAvailable);
        formDataToSend.append(
          "ln_sitevisit_avail",
          formData.siteVisitAvailable
        );
        formDataToSend.append("ln_soiltype", formData.soilType);
        formDataToSend.append(
          "ln_dimension_east",
          formData.plotDimensions.east
        );
        formDataToSend.append(
          "ln_dimension_west",
          formData.plotDimensions.west
        );
        formDataToSend.append(
          "ln_dimension_north",
          formData.plotDimensions.north
        );
        formDataToSend.append(
          "ln_dimension_south",
          formData.plotDimensions.south
        );
        formDataToSend.append(
          "ln_allowed_floors",
          formData.numberOfFloors || "0"
        );
        formDataToSend.append(
          "ln_sitevisit_link",
          formData.siteVisitLink || ""
        );

        // Append the image file if new one is uploaded
        if (formData.plotImage && formData.plotImage.length > 0) {
          formData.plotImage.forEach((file) => {
            formDataToSend.append("ln_image", file);
          });
        }
        if (existingImages.length > 0) {
          existingImages.forEach((img) => {
            formDataToSend.append("ln_existing_image", img);
          });
        }

        // Append the video file if new one is uploaded
        if (formData.plotVideo && formData.plotVideo.length > 0) {
          formData.plotVideo.forEach((file) => {
            formDataToSend.append("ln_video", file);
          });
        }
        if (existingVideos.length > 0) {
          existingVideos.forEach((vid) => {
            formDataToSend.append("ln_existing_video", vid);
          });
        }

        if (formData.droneFootage && formData.droneFootage.length > 0) {
          formData.droneFootage.forEach((file) => {
            formDataToSend.append("ln_drone_vid", file);
          });
        } else if (existingDroneFootage) {
          formDataToSend.append(
            "ln_existing_drone_footage",
            existingDroneFootage
          );
        }

        formDataToSend.append(
          "ln_drone_footage_type",
          formData.droneFootageType || existingDroneFootageType || ""
        );

        if (formData.encumbranceCertificateFile) {
          formDataToSend.append(
            "ln_encumb_cert",
            formData.encumbranceCertificateFile
          );
        }

        formDataToSend.forEach((value, key) => {
          if (value instanceof File) {
            console.log(`${key}: ${value.name}`);
          } else {
            console.log(`${key}: ${value}`);
          }
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/landadd/update/${id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formDataToSend,
          }
        );

        const data = await response.json();
        if (data.success) {
          navigate("/authuser/My-Lands", {
            state: { message: data.message || "Land updated successfully!" },
          });
        } else {
          throw new Error(
            data.error || data.message || "Failed to update land"
          );
        }
      } catch (error: any) {
        console.error("Error updating land:", error);
        toast.error(error.message || "Error updating land. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.error(
        "Please complete all required fields in the current step before submitting."
      );
    }
  };

  const renderStep1 = () => (
    <div className="row form">
      <div className="col-lg-3 pb-5">
        <Uploader
          label="Upload plot image (Max 5mb each)"
          name="plotImage"
          accept="image/*"
          multiple
          previews={[...formData.plotImagePreview, ...existingImages]}
          previewType="image"
          onChange={handleFileChange}
          inputRef={plotImageInputRef}
          // required={true}
          // onDelete={(index) => {
          //   if (index < existingImages.length) {
          //     handleDeleteExistingImage(index);
          //   } else {
          //     handleDeleteFile("plotImage", index - existingImages.length);
          //   }
          // }}
        />
        {errors.plotImage && (
          <span className="text-danger">{errors.plotImage}</span>
        )}
      </div>
      <div className="col-lg-3 pb-5">
        <Uploader
          label="Upload plot video (Max 5mb each)"
          name="plotVideo"
          accept="video/*"
          multiple
          previews={[...formData.plotVideoPreview]}
          // previews={[...formData.plotVideoPreview, ...existingVideos]}
          previewType="video"
          onChange={handleFileChange}
          inputRef={plotVideoInputRef}
          // onDelete={(index) => {
          //   if (index < existingVideos.length) {
          //     handleDeleteExistingVideo(index);
          //   } else {
          //     handleDeleteFile("plotVideo", index - existingVideos.length);
          //   }
          // }}
        />
        {errors.plotVideo && (
          <span className="text-danger">{errors.plotVideo}</span>
        )}
      </div>
      <div className="col-lg-3 pb-5">
        <Uploader
          label="Upload 360° View / Drone Footage"
          name="droneFootage"
          accept="video/*"
          previews={
            formData.droneFootagePreview.length > 0
              ? formData.droneFootagePreview
              : existingDroneFootage
              ? [existingDroneFootage]
              : []
          }
          previewType="video"
          onChange={handleFileChange}
          inputRef={droneFootageInputRef}
          // onDelete={() => {
          //   if (existingDroneFootage) {
          //     handleDeleteExistingDroneFootage();
          //   } else {
          //     setFormData((prev) => ({
          //       ...prev,
          //       droneFootage: [],
          //       droneFootagePreview: [],
          //       droneFootageType: "",
          //     }));
          //   }
          // }}
        />
      </div>
      <div className="col-lg-3 pb-5">
        <div className="box-fieldset">
          <label>
            Post Title
            <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="postTitle"
            value={formData.postTitle}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Enter title"
            maxLength={150}
          />
          {errors.postTitle && (
            <span className="text-danger">{errors.postTitle}</span>
          )}
        </div>
      </div>
      <div className="col-lg-3 pb-5">
        <div className="box-fieldset">
          <label>
            Posted Date
            <span style={{ color: "red" }}>*</span>
          </label>
          <div className="box-ip">
            <input
              type="date"
              name="postedDate"
              value={formData.postedDate}
              readOnly
              onChange={handleInputChange}
              className="form-control"
            />
            {errors.postedDate && (
              <span className="text-danger">{errors.postedDate}</span>
            )}
          </div>
        </div>
      </div>
      <div className="col-lg-3 pb-5">
        <div className="box-fieldset">
          <label>
            Plot Area(sq. ft.)
            <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="totalareasquarefeet"
            value={formData.totalareasquarefeet}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Total area sq ft"
            maxLength={100}
          />
          {errors.totalareasquarefeet && (
            <span className="text-danger">{errors.totalareasquarefeet}</span>
          )}
        </div>
      </div>
      <div className="col-lg-3 pb-5">
        <div className="box-fieldset">
          <label>
            Price
            <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Price"
            maxLength={100}
          />
          {errors.price && <span className="text-danger">{errors.price}</span>}
        </div>
      </div>
      <div className="col-lg-3 pb-5">
        <div className="box-fieldset">
          <label>
            Price Per Sq. Ft.
            <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="totalPriceSqft"
            value={formData.totalPriceSqft}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Price per sq ft"
            readOnly
            maxLength={100}
          />
          {errors.totalPriceSqft && (
            <span className="text-danger">{errors.totalPriceSqft}</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <>
      <div className="row form pb-4">
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Plot Type
              <span style={{ color: "red" }}>*</span>
            </label>
            <select
              name="plotType"
              value={formData.plotType}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select</option>
              {apiData?.plotTypes
                ?.filter((plot) => plot.pt_status === 1)
                .map((plot) => (
                  <option
                    key={plot.pt_id}
                    value={`${plot.pt_name}`} // ✅ ID and name combined
                  >
                    {plot.pt_name}
                  </option>
                ))}
            </select>
            {errors.plotType && (
              <span className="text-danger">{errors.plotType}</span>
            )}
          </div>
        </div>

        <div className="col-lg-3 pb-4">
          <div className="box-fieldset">
            <label>
              Zone
              <span style={{ color: "red" }}>*</span>
            </label>
            <select
              name="zone"
              value={formData.zone}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select</option>
              {apiData?.zones
                ?.filter((zone) => zone["Z-status"] === 1)
                .map((zone) => (
                  <option key={zone.z_id} value={zone.z_name}>
                    {zone.z_name}
                  </option>
                ))}
            </select>
            {errors.zone && <span className="text-danger">{errors.zone}</span>}
          </div>
        </div>
        <div className="col-lg-3 pb-4">
          <div className="box-radio-check mb-16">
            <label>
              Seller Type
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Individual</span>
                  <input
                    type="radio"
                    name="sellerType"
                    value="individual"
                    checked={formData.sellerType === "individual"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Multiple</span>
                  <input
                    type="radio"
                    name="sellerType"
                    value="multiple"
                    checked={formData.sellerType === "multiple"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.sellerType && (
              <span className="text-danger">{errors.sellerType}</span>
            )}
          </div>
        </div>
        <div className="col-lg-2 pb-5">
          <div className="box-radio-check mb-16">
            <label>
              Transaction Type (Resale)
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="transactionType"
                    value="yes"
                    checked={formData.transactionType === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="transactionType"
                    value="no"
                    checked={formData.transactionType === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.transactionType && (
              <span className="text-danger">{errors.transactionType}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Plot Dimensions in Feet
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="input-group">
              <input
                type="text"
                name="plotDimensions.east"
                value={formData.plotDimensions.east}
                onChange={handleInputChange}
                placeholder="East"
                className="form-control"
              />
              <input
                type="text"
                name="plotDimensions.west"
                value={formData.plotDimensions.west}
                onChange={handleInputChange}
                placeholder="West"
                className="form-control"
              />
              <input
                type="text"
                name="plotDimensions.north"
                value={formData.plotDimensions.north}
                onChange={handleInputChange}
                placeholder="North"
                className="form-control"
              />
              <input
                type="text"
                name="plotDimensions.south"
                value={formData.plotDimensions.south}
                onChange={handleInputChange}
                placeholder="South"
                className="form-control"
              />
            </div>

            {errors.plotDimensions && (
              <span className="text-danger">{errors.plotDimensions}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Possession Status
              {/* <span style={{ color: "red" }}>*</span> */}
            </label>
            <input
              type="text"
              name="possessionStatus"
              value={formData.possessionStatus}
              onChange={handleInputChange}
              className="form-control"
              maxLength={50}
            />
            {errors.possessionStatus && (
              <span className="text-danger">{errors.possessionStatus}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Width of Road Facing
              <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              name="widthOfRoadFacing"
              value={formData.widthOfRoadFacing}
              onChange={handleInputChange}
              placeholder="in feet"
              className="form-control"
              maxLength={100}
            />
            {errors.widthOfRoadFacing && (
              <span className="text-danger">{errors.widthOfRoadFacing}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Overlooking
              {/* <span style={{ color: "red" }}>*</span> */}
            </label>
            <input
              type="text"
              name="overlooking"
              value={formData.overlooking}
              onChange={handleInputChange}
              placeholder="e.g main road"
              className="form-control"
              maxLength={100}
            />
            {errors.overlooking && (
              <span className="text-danger">{errors.overlooking}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>Number of Floors Allowed (Optional)</label>
            <input
              type="text"
              name="numberOfFloors"
              value={formData.numberOfFloors}
              onChange={handleInputChange}
              placeholder="e.g. 3"
              className="form-control"
              maxLength={50}
            />
            {errors.numberOfFloors && (
              <span className="text-danger">{errors.numberOfFloors}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-radio-check mb-16">
            <label>
              Boundary Wall
              {/* <span style={{ color: "red" }}>*</span> */}
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="boundaryWall"
                    value="Yes"
                    checked={formData.boundaryWall === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="boundaryWall"
                    value="No"
                    checked={formData.boundaryWall === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.boundaryWall && (
              <span className="text-danger">{errors.boundaryWall}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Facing Direction
              <span style={{ color: "red" }}>*</span>
            </label>
            <select
              name="facingDirection"
              value={formData.facingDirection}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select</option>
              {apiData?.facing
                ?.filter((face) => face.fc_status === 1)
                .map((face) => (
                  <option key={face.fc_id} value={face.fc_name}>
                    {face.fc_name}
                  </option>
                ))}
            </select>
            {errors.facingDirection && (
              <span className="text-danger">{errors.facingDirection}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-radio-check mb-16">
            <label>
              Corner Plot
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="cornerPlot"
                    value="Yes"
                    checked={formData.cornerPlot === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="cornerPlot"
                    value="No"
                    checked={formData.cornerPlot === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.cornerPlot && (
              <span className="text-danger">{errors.cornerPlot}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Fencing Type
              {/* <span style={{ color: "red" }}>*</span> */}
            </label>
            <select
              name="fencingType"
              value={formData.fencingType}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select</option>
              {apiData?.fencing
                ?.filter((fence) => fence.fnc_status === 1)
                .map((fence) => (
                  <option key={fence.fnc_id} value={fence.fnc_name}>
                    {fence.fnc_name}
                  </option>
                ))}
            </select>
            {errors.fencingType && (
              <span className="text-danger">{errors.fencingType}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-radio-check mb-16">
            <label>
              Electricity & Water Supply Availability
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="utilitiesAvailability"
                    value="Yes"
                    checked={formData.utilitiesAvailability === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="utilitiesAvailability"
                    value="No"
                    checked={formData.utilitiesAvailability === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.utilitiesAvailability && (
              <span className="text-danger">
                {errors.utilitiesAvailability}
              </span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Topography
              {/* <span style={{ color: "red" }}>*</span> */}
            </label>
            <select
              name="topography"
              value={formData.topography}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select</option>
              {apiData?.topography
                ?.filter((topo) => topo.tp_status === 1)
                .map((topo) => (
                  <option key={topo.tp_id} value={topo.tp_name}>
                    {topo.tp_name}
                  </option>
                ))}
            </select>
            {errors.topography && (
              <span className="text-danger">{errors.topography}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-radio-check mb-16">
            <label>
              Flood Zone Risk / Water Logging
              {/* <span style={{ color: "red" }}>*</span> */}
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="floodZoneRisk"
                    value="Yes"
                    checked={formData.floodZoneRisk === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="floodZoneRisk"
                    value="No"
                    checked={formData.floodZoneRisk === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.floodZoneRisk && (
              <span className="text-danger">{errors.floodZoneRisk}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>RERA Number (Optional)</label>
            <input
              type="text"
              name="reraNumber"
              value={formData.reraNumber}
              onChange={handleInputChange}
              placeholder="Enter RERA Number"
              className="form-control"
              maxLength={100}
            />
            {errors.reraNumber && (
              <span className="text-danger">{errors.reraNumber}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-radio-check mb-16">
            <label>
              Authority Approved
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="authorityApproved"
                    value="Yes"
                    checked={formData.authorityApproved === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="authorityApproved"
                    value="No"
                    checked={formData.authorityApproved === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.authorityApproved && (
              <span className="text-danger">{errors.authorityApproved}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Encumbrance Certificate
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="input-group">
              <select
                name="encumbranceCertificate"
                value={formData.encumbranceCertificate}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Select Status</option>
                <option value="clear">Clear</option>
                <option value="pending">Pending</option>
                <option value="unknown">Unknown</option>
              </select>
              <input
                type="file"
                name="encumbranceCertificateFile"
                onChange={handleFileChange}
                accept=".pdf"
                className="form-control"
                ref={encumbranceCertificateInputRef}
              />
            </div>
            {errors.encumbranceCertificate && (
              <span className="text-danger">
                {errors.encumbranceCertificate}
              </span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-radio-check mb-16">
            <label>
              Mutation Done
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="mutationDone"
                    value="Yes"
                    checked={formData.mutationDone === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="mutationDone"
                    value="No"
                    checked={formData.mutationDone === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.mutationDone && (
              <span className="text-danger">{errors.mutationDone}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-radio-check mb-16">
            <label>
              Patta Available
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="pattaAvailable"
                    value="Yes"
                    checked={formData.pattaAvailable === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="pattaAvailable"
                    value="No"
                    checked={formData.pattaAvailable === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.pattaAvailable && (
              <span className="text-danger">{errors.pattaAvailable}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-5">
          <div className="box-fieldset">
            <label>
              Soil Type
              {/* <span style={{ color: "red" }}>*</span> */}
            </label>
            <select
              name="soilType"
              value={formData.soilType}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select</option>
              <option value="alluvial">Alluvial</option>
              <option value="laterite">Laterite</option>
              <option value="red">Red</option>
              <option value="sandy">Sandy</option>
              <option value="clay">Clay</option>
            </select>
            {errors.soilType && (
              <span className="text-danger">{errors.soilType}</span>
            )}
          </div>
        </div>
      </div>
      <div className="row form pb-4">
        <div className="col-lg-6 pb-4">
          <div className="box-fieldset">
            <label>
              Plot Description
              <span style={{ color: "red" }}>*</span>
            </label>
            <textarea
              name="plotDescription"
              value={formData.plotDescription}
              onChange={handleInputChange}
              className="textarea"
            ></textarea>
            {errors.plotDescription && (
              <span className="text-danger">{errors.plotDescription}</span>
            )}
          </div>
        </div>
        <div className="col-lg-6 pb-4">
          <div className="box-fieldset">
            <label>
              Growth Opportunity (upcoming features for that plot/land)
            </label>
            <textarea
              name="growthPotentials"
              value={formData.growthPotentials}
              onChange={handleInputChange}
              className="textarea"
            ></textarea>
          </div>
        </div>
      </div>
      <div className="row form pb-4">
        <div className="col-lg-3 pb-2">
          <div className="box-radio-check mb-16">
            <label>
              Any Construction Done
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="anyConstructionDone"
                    value="Yes"
                    checked={formData.anyConstructionDone === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="anyConstructionDone"
                    value="No"
                    checked={formData.anyConstructionDone === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.anyConstructionDone && (
              <span className="text-danger">{errors.anyConstructionDone}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-2">
          <div className="box-fieldset">
            <label>
              Status
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="checkbox-item style-1 me-4">
                <label>
                  <span className="text-1">Legal</span>
                  <input
                    type="checkbox"
                    name="legal"
                    checked={formData.status.legal}
                    onChange={handleInputChange}
                  />
                  <span className="btn-checkbox"></span>
                </label>
              </fieldset>
              <fieldset className="checkbox-item style-1 me-4">
                <label>
                  <span className="text-1">Infra</span>
                  <input
                    type="checkbox"
                    name="infra"
                    checked={formData.status.infra}
                    onChange={handleInputChange}
                  />
                  <span className="btn-checkbox"></span>
                </label>
              </fieldset>
            </div>
            {errors.status && (
              <span className="text-danger">{errors.status}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-2">
          <div className="box-radio-check mb-16">
            <label>
              Loan Available
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="loanAvailable"
                    value="Yes"
                    checked={formData.loanAvailable === "Yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">No</span>
                  <input
                    type="radio"
                    name="loanAvailable"
                    value="No"
                    checked={formData.loanAvailable === "No"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.loanAvailable && (
              <span className="text-danger">{errors.loanAvailable}</span>
            )}
          </div>
        </div>
        <div className="col-lg-3 pb-2">
          <div className="box-radio-check mb-16">
            <label>
              Land Availability
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Yes</span>
                  <input
                    type="radio"
                    name="landAvailability"
                    value="yes"
                    checked={formData.landAvailability === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
              <fieldset className="radio-item style-1 me-4">
                <label>
                  <span className="text-1">Sold Out</span>
                  <input
                    type="radio"
                    name="landAvailability"
                    value="soldout"
                    checked={formData.landAvailability === "soldout"}
                    onChange={handleInputChange}
                  />
                  <span className="btn-radio"></span>
                </label>
              </fieldset>
            </div>
            {errors.landAvailability && (
              <span className="text-danger">{errors.landAvailability}</span>
            )}
          </div>
        </div>
      </div>
      <div className="row form pb-4">
        <div className="col-lg-7 pb-2">
          <div className="box-radio-check mb-16">
            <label>
              Classification
              <span style={{ color: "red" }}>*</span>
            </label>
            <div className="d-flex justify-content-start">
              {apiData?.classifications
                ?.filter((cls) => cls.cls_status === 1)
                .map((cls) => (
                  <fieldset
                    key={cls.cls_id}
                    className="radio-item style-1 me-4"
                  >
                    <label>
                      <span className="text-1">{cls.cls_name}</span>
                      <input
                        type="radio"
                        name="classification"
                        value={cls.cls_name}
                        checked={formData.classification === cls.cls_name}
                        onChange={handleInputChange}
                      />
                      <span className="btn-radio"></span>
                    </label>
                  </fieldset>
                ))}
            </div>
            {errors.classification && (
              <span className="text-danger">{errors.classification}</span>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const renderStep3 = () => (
    <div className="row form">
      <div className="col-lg-4 pb-5">
        <div className="box-fieldset">
          <label>
            Locality
            <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="locality"
            value={formData.locality}
            onChange={handleInputChange}
            className="form-control"
            maxLength={100}
          />
          {errors.locality && (
            <span className="text-danger">{errors.locality}</span>
          )}
        </div>
      </div>
      <div className="col-lg-4 pb-5">
        <div className="box-fieldset">
          <label>
            Nearby Landmark
            <span style={{ color: "red" }}>*</span>
          </label>
          <select
            name="nearbyLandmark"
            value={formData.nearbyLandmark}
            onChange={handleInputChange}
            className="form-control"
          >
            <option value="">Select or Enter Custom</option>
            <option value="hospital">Hospital</option>
            <option value="school">School</option>
            <option value="mall">Mall</option>
            <option value="park">Park</option>
            <option value="custom">Custom</option>
          </select>
          {formData.nearbyLandmark === "custom" && (
            <input
              type="text"
              name="customLandmark"
              value={
                formData.customLandmark === "custom"
                  ? ""
                  : formData.customLandmark
              }
              onChange={handleInputChange}
              placeholder="Enter custom landmark"
              className="form-control mt-2"
              maxLength={100}
            />
          )}
          {errors.nearbyLandmark && (
            <span className="text-danger">{errors.nearbyLandmark}</span>
          )}
        </div>
      </div>
      <div className="col-lg-12 pb-5">
        <div className="box-fieldset">
          <label>
            Address
            <span style={{ color: "red" }}>*</span>
          </label>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className="textarea"
          ></textarea>
          {errors.address && (
            <span className="text-danger">{errors.address}</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="row form">
      <div className="col-lg-12 pb-5">
        <div className="box-fieldset">
          <label>Highlights</label>
          <textarea
            name="highlights"
            value={formData.highlights}
            onChange={handleInputChange}
            className="textarea"
            placeholder="e.g. Capital Hospital within 1km"
          ></textarea>
        </div>
      </div>
      <div className="col-lg-4 pb-5">
        <div className="box-fieldset">
          <label>
            Map
            {/* <span style={{ color: "red" }}>*</span> */}
          </label>
          <div className="input-group">
            <input
              type="text"
              name="longitude"
              value={formData.longitude}
              onChange={handleInputChange}
              placeholder="Longitude"
              className="form-control"
            />
            <input
              type="text"
              name="latitude"
              value={formData.latitude}
              onChange={handleInputChange}
              placeholder="Latitude"
              className="form-control"
            />
          </div>
          {errors.map && <span className="text-danger">{errors.map}</span>}
        </div>
      </div>

      <div className="col-lg-3 pb-4">
        <div className="box-radio-check mb-16">
          <label>
            Gated Society
            <span style={{ color: "red" }}>*</span>
          </label>
          <div className="d-flex justify-content-start">
            <fieldset className="radio-item style-1 me-4">
              <label>
                <span className="text-1">Yes</span>
                <input
                  type="radio"
                  name="gatedSociety"
                  value="Yes"
                  checked={formData.gatedSociety === "Yes"}
                  onChange={handleInputChange}
                />
                <span className="btn-radio"></span>
              </label>
            </fieldset>
            <fieldset className="radio-item style-1 me-4">
              <label>
                <span className="text-1">No</span>
                <input
                  type="radio"
                  name="gatedSociety"
                  value="No"
                  checked={formData.gatedSociety === "No"}
                  onChange={handleInputChange}
                />
                <span className="btn-radio"></span>
              </label>
            </fieldset>
            {errors.gatedSociety && (
              <span className="text-danger">{errors.gatedSociety}</span>
            )}
          </div>
        </div>
      </div>
      <div className="col-lg-3 pb-4">
        <div className="box-radio-check mb-16">
          <label>
            Site Visit Available
            <span style={{ color: "red" }}>*</span>
          </label>
          <div className="d-flex justify-content-start">
            <fieldset className="radio-item style-1 me-4">
              <label>
                <span className="text-1">Yes</span>
                <input
                  type="radio"
                  name="siteVisitAvailable"
                  value="Yes"
                  checked={formData.siteVisitAvailable === "Yes"}
                  onChange={handleInputChange}
                />
                <span className="btn-radio"></span>
              </label>
            </fieldset>
            <fieldset className="radio-item style-1 me-4">
              <label>
                <span className="text-1">No</span>
                <input
                  type="radio"
                  name="siteVisitAvailable"
                  value="No"
                  checked={formData.siteVisitAvailable === "No"}
                  onChange={handleInputChange}
                />
                <span className="btn-radio"></span>
              </label>
            </fieldset>
          </div>
          {formData.siteVisitAvailable === "Yes" && (
            <div className="box-fieldset mt-2">
              <label>Site Visit Link(Location Link)</label>
              <input
                type="text"
                name="siteVisitLink"
                value={formData.siteVisitLink}
                onChange={handleInputChange}
                placeholder="Enter link"
                className="form-control"
              />
              {errors.siteVisitLink && (
                <span className="text-danger">{errors.siteVisitLink}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  return (
    <FrontendLayout>
      <ToastContainer />
      <div className="user">
        <div className="tf-container py-5 user">
          <UserCommon
            name={profile.name}
            email={profile.email}
            imageUrl={profile.imageUrl}
            userType={profile?.userType}
          />

          <div className="tab" role="tabpanel">
            <UserTabs />

            <div className="tab-content tabs">
              <div role="tabpanel" className="tab-pane in active">
                <ProgressBar
                  currentStep={currentStep}
                  totalSteps={4}
                  onStepClick={handleStepClick} // Pass the handler
                />
                <hr />

                {renderCurrentStep()}

                <div className="box">
                  <div className="d-flex justify-content-between gap-2">
                    {currentStep > 1 && (
                      <button
                        onClick={handlePrevious}
                        className="tf-btn style-border pd-20"
                      >
                        <i className="bi bi-arrow-left"></i>Back
                      </button>
                    )}
                    {currentStep < 4 ? (
                      <button
                        onClick={handleNext}
                        className="tf-btn bg-color-primary pd-20"
                      >
                        Continue<i className="bi bi-arrow-right"></i>
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        className="tf-btn bg-color-primary pd-20"
                      >
                        Update
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CommonAlertModal
        show={alertModal.show}
        message={alertModal.message}
        onClose={() => setAlertModal({ show: false, message: "" })}
      />
    </FrontendLayout>
  );
};

export default EditLand;
